import type { Archive, MaterialTag } from "../../domain/types";
import { acquireItem, consumeItem } from "../../domain/itemRegistry";
import {
  completeTaskNarratively,
  sanitizeProposedTask,
} from "../../domain/tasks";
import { parseStateCommands } from "../stateCommand/parser";
import { setNestedValue, type ParsedStateCommand } from "../stateCommandParser";
import {
  normalizeBoundedStat,
  normalizeMhCharacter,
  normalizeMonsterIndividual,
} from "../../domain/actors";
import type { AutoContinuePayload } from "../../domain/types";
import { parseBranchOptions } from "../../features/branch-options/logic";

export function applyStateCommand(
  archive: Archive,
  command: ParsedStateCommand,
): Archive {
  let next = archive;
  if (command.kind === "acquireItem")
    next.itemRegistry = acquireItem(
      next.itemRegistry,
      {
        key: command.key,
        name: command.name,
        description: "叙事中取得的物品。",
        meta: {
          kind: "material",
          rarity: command.rarity,
          tags: command.tags as MaterialTag[],
        },
      },
      command.quantity,
      "叙事获得",
    );
  if (command.kind === "consumeItem")
    next.itemRegistry = consumeItem(
      next.itemRegistry,
      command.key,
      command.quantity,
    );
  if (
    command.kind === "completeTask" &&
    command.taskId === next.tasks.activeTaskId
  )
    next = completeTaskNarratively(next, command.taskId);
  if (
    command.kind === "failTask" &&
    command.taskId === next.tasks.activeTaskId
  ) {
    const index = next.tasks.available.findIndex(
      (task) => task.id === command.taskId,
    );
    if (index >= 0) {
      const [task] = next.tasks.available.splice(index, 1);
      task.status = "failed";
      next.tasks.history.push(task);
    }
    next.tasks.activeTaskId = null;
  }
  if (command.kind === "proposeTask") {
    if (!next.player.profile.profession.hasHunterLicense) return next;
    if (
      next.tasks.available.filter(
        (task) => task.source === "generated" && task.status === "available",
      ).length >= 2
    )
      return next;
    const value = command.value;
    if (
      typeof value.title !== "string" ||
      typeof value.description !== "string" ||
      typeof value.questType !== "string"
    )
      throw new Error("动态任务缺少 title/description/questType");
    const questType = [
      "hunt",
      "capture",
      "gather",
      "deliver",
      "special",
    ].includes(value.questType)
      ? (value.questType as
          "hunt" | "capture" | "gather" | "deliver" | "special")
      : "special";
    const star = typeof value.star === "number" ? value.star : 1,
      n = next.tasks.available.length + next.tasks.history.length + 1;
    next.tasks.available.push(
      sanitizeProposedTask(
        {
          id: `T_G_${n}`,
          source: "generated",
          title: value.title,
          description: value.description,
          questType,
          star,
          targetMonsterIds: Array.isArray(value.targetMonsterIds)
            ? value.targetMonsterIds.map(String)
            : [],
          targetMonsterNames: Array.isArray(value.targetMonsterNames)
            ? value.targetMonsterNames.map(String)
            : [],
          location:
            typeof value.location === "string"
              ? value.location
              : "公会指定区域",
          contractFee: star * 40,
          status: "available",
          reward: {
            zenny: star * 420,
            hrExp: star * 35,
            guildPoints: star * 10,
          },
          settlementDone: false,
        },
        next.player.hunter.hr,
      ),
    );
  }
  if (command.kind === "transferZenny") {
    if (command.fromId === command.toId) return next;
    if (command.fromId === "B1") {
      if (next.player.wallet.zenny < command.amount)
        throw new Error("玩家泽尼不足，无法完成转移");
      next.player.wallet.zenny -= command.amount;
    }
    if (command.toId === "B1") next.player.wallet.zenny += command.amount;
  }
  if (command.kind === "advanceTime") {
    next.gameTime.hour += command.hours;
    next.gameTime.day += Math.floor(next.gameTime.hour / 24);
    next.gameTime.hour %= 24;
  }
  if (command.kind === "monsterState") {
    const old =
      next.monsters[command.monsterId] ??
      normalizeMonsterIndividual(command.monsterId, {
        id: command.monsterId,
        name: String(command.value.name ?? command.monsterId),
      });
    const broken = Array.isArray(command.value.brokenParts)
      ? command.value.brokenParts.map(String)
      : [];
    next.monsters[command.monsterId] = {
      ...old,
      name: String(command.value.name ?? old.name),
      speciesId: String(command.value.speciesId ?? old.speciesId),
      individualName: String(
        command.value.individualName ?? old.individualName,
      ),
      health:
        typeof command.value.health === "number"
          ? command.value.health
          : old.health,
      rage:
        typeof command.value.rage === "number" ? command.value.rage : old.rage,
      fatigue:
        typeof command.value.fatigue === "number"
          ? command.value.fatigue
          : old.fatigue,
      tailSevered:
        typeof command.value.tailSevered === "boolean"
          ? command.value.tailSevered
          : old.tailSevered,
      location: String(command.value.location ?? old.location),
      scars: Array.isArray(command.value.scars)
        ? command.value.scars.map(String)
        : old.scars,
      territory: Array.isArray(command.value.territory)
        ? command.value.territory.map(String)
        : old.territory,
      lastSeenAt: String(command.value.lastSeenAt ?? old.lastSeenAt),
      status: ["active", "fled", "captured", "slain", "unknown"].includes(
        String(command.value.status),
      )
        ? (String(command.value.status) as typeof old.status)
        : old.status,
      parts: Object.fromEntries(
        Object.entries(old.parts).map(([key, value]) => [
          key,
          { ...value, broken: value.broken || broken.includes(key) },
        ]),
      ),
    };
  }
  if (command.kind === "upsertCharacter") {
    const old = next.characters[command.character.id];
    next.characters[command.character.id] = normalizeMhCharacter(
      command.character.id,
      old
        ? { ...old, ...command.character, legacy: old.legacy }
        : command.character,
      next.scene.location,
    );
  }
  if (command.kind === "set")
    next = setNestedValue(next, command.path, command.value) as Archive;
  if (command.kind === "increment") {
    const current = command.path
      .split(".")
      .reduce<unknown>(
        (value, key) =>
          value && typeof value === "object"
            ? (value as Record<string, unknown>)[key]
            : undefined,
        next,
      );
    next = setNestedValue(
      next,
      command.path,
      Number(current ?? 0) + command.value,
    ) as Archive;
  }
  if (command.kind === "append") {
    const current = command.path
      .split(".")
      .reduce<unknown>(
        (value, key) =>
          value && typeof value === "object"
            ? (value as Record<string, unknown>)[key]
            : undefined,
        next,
      );
    next = setNestedValue(next, command.path, [
      ...(Array.isArray(current) ? current : []),
      command.value,
    ]) as Archive;
  }
  const affinityPath =
    command.kind === "set" || command.kind === "increment"
      ? command.path.match(
          /^characters\.(C\d+)\.relations\.(\d+)\.affinity\.current$/,
        )
      : null;
  if (affinityPath) {
    const relation =
      next.characters[affinityPath[1]]?.relations[Number(affinityPath[2])];
    if (relation)
      relation.affinity = normalizeBoundedStat(relation.affinity.current);
  }
  next.player.vitals.health = Math.max(
    0,
    Math.min(100, next.player.vitals.health),
  );
  next.player.vitals.stamina = Math.max(
    0,
    Math.min(100, next.player.vitals.stamina),
  );
  return next;
}

export function applyPostTurn(
  archive: Archive,
  input: string,
  raw: string,
  options: {
    turnId?: string;
    snapshotId?: string;
    autoContinue?: AutoContinuePayload;
  } = {},
) {
  const timeSkip = options.autoContinue?.mode === "timeSkip";
  const parsed = parseStateCommands(raw, timeSkip ? 8760 : 24);
  if (timeSkip) {
    const advances = parsed.commands.filter(
      (command) => command.kind === "advanceTime" && command.hours > 0,
    );
    if (advances.length !== 1)
      throw new Error("时光流转必须且只能提交一次有效的时间推进");
  }
  let next = structuredClone(archive);
  for (const command of parsed.commands)
    try {
      next = applyStateCommand(next, command);
    } catch (error) {
      parsed.errors.push(
        error instanceof Error ? error.message : String(error),
      );
    }
  const now = Date.now();
  const branches = parseBranchOptions(parsed.visible);
  const displayInput = options.autoContinue
    ? `【${options.autoContinue.displayLabel}】`
    : input;
  next.messages.push(
    {
      id: `U_${now}`,
      role: "user",
      content: displayInput,
      createdAt: now,
      turnId: options.turnId,
      snapshotId: options.snapshotId,
      turnInput: { content: input, autoContinue: options.autoContinue },
    },
    {
      id: `A_${now + 1}`,
      role: "assistant",
      content: branches.visible,
      createdAt: now + 1,
      turnId: options.turnId,
      snapshotId: options.snapshotId,
      branchOptions: branches.options.length ? branches.options : undefined,
      turnInput: { content: input, autoContinue: options.autoContinue },
    },
  );
  next.pendingCraftingContext = null;
  next.pendingHuntContext = null;
  return { archive: next, visible: branches.visible, errors: parsed.errors };
}
